'use client';

import * as React from 'react';
import type { CultivDash } from '../types';
import { MARKETING_CDN } from '../site';
import './dashboard-mock.css';

/* Scene-4 dashboard loop — animated CRM-dashboard mock (commissions + forecast),
   ported from the approved design handoff. The artwork is a fixed 1680×881
   canvas (SVG charts share its pixel grid), scaled to fit the parent box and
   centered; the surround is the canvas's own #F4F3F0 so letterboxing is
   invisible. Icon↔label rows carry .kq-cap per the platform ink-alignment rule. */

/* lucide outlines used by the mock, inlined once as symbols */
export function MockDefs() {
  const p = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
  return (
    <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }} aria-hidden="true">
      <defs>
        <symbol id="s4d-chevron-down" viewBox="0 0 24 24" {...p}><path d="m6 9 6 6 6-6" /></symbol>
        <symbol id="s4d-funnel" viewBox="0 0 24 24" {...p}><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" /></symbol>
        <symbol id="s4d-trending-up" viewBox="0 0 24 24" {...p}><path d="M16 7h6v6" /><path d="m22 7-8.5 8.5-5-5L2 17" /></symbol>
        <symbol id="s4d-chart-column" viewBox="0 0 24 24" {...p}><path d="M3 3v16a2 2 0 0 0 2 2h16" /><rect x="15" y="5" width="4" height="12" rx="1" /><rect x="7" y="8" width="4" height="9" rx="1" /></symbol>
        <symbol id="s4d-search" viewBox="0 0 24 24" {...p}><path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" /></symbol>
        <symbol id="s4d-gantt" viewBox="0 0 24 24" {...p}><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 8h7" /><path d="M8 12h6" /><path d="M11 16h5" /></symbol>
        <symbol id="s4d-folder-input" viewBox="0 0 24 24" {...p}><path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1" /><path d="M2 13h10" /><path d="m9 16 3-3-3-3" /></symbol>
        <symbol id="s4d-table" viewBox="0 0 24 24" {...p}><path d="M15 3v18" /><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M21 9H3" /><path d="M21 15H3" /></symbol>
        <symbol id="s4d-calendar" viewBox="0 0 24 24" {...p}><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4" /><path d="M3 10h18" /><path d="M8 2v4" /><path d="M17 14h-6" /><path d="M13 18H7" /><path d="M7 14h.01" /><path d="M17 18h.01" /></symbol>
        <symbol id="s4d-file-chart" viewBox="0 0 24 24" {...p}><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /><path d="M14 2v5a1 1 0 0 0 1 1h5" /><path d="M8 18v-1" /><path d="M12 18v-6" /><path d="M16 18v-3" /></symbol>
        <symbol id="s4d-file-pen" viewBox="0 0 24 24" {...p}><path d="m18 5-2.414-2.414A2 2 0 0 0 14.172 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /><path d="M21.378 12.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" /></symbol>
        <symbol id="s4d-workflow" viewBox="0 0 24 24" {...p}><rect width="8" height="8" x="3" y="3" rx="2" /><path d="M7 11v4a2 2 0 0 0 2 2h4" /><rect width="8" height="8" x="13" y="13" rx="2" /></symbol>
        <symbol id="s4d-settings" viewBox="0 0 24 24" {...p}><path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" /><circle cx="12" cy="12" r="3" /></symbol>
        <symbol id="s4d-mail-question" viewBox="0 0 24 24" {...p}><path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h12.5" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /><path d="M18 15.28c.2-.4.5-.8.9-1a2.1 2.1 0 0 1 2.6.4c.3.4.5.8.5 1.3 0 1.3-2 2-2 2" /><path d="M20 22v.01" /></symbol>
      </defs>
    </svg>
  );
}

export function MockIcon({ id, s = 15 }: { id: string; s?: number }) {
  return (
    <svg width={s} height={s}>
      <use href={`#s4d-${id}`} />
    </svg>
  );
}

function NavItem({
  icon,
  label,
  count,
  red,
  on,
}: {
  icon: string;
  label: string;
  count?: string;
  red?: boolean;
  on?: boolean;
}) {
  return (
    <li>
      <a href="#" onClick={(e) => e.preventDefault()} className={'s4d-item' + (on ? ' on' : '')}>
        <span className="ic"><MockIcon id={icon} /></span>
        <span className="lb kq-cap">{label}</span>
        {count && <span className={'ct kq-cap' + (red ? ' red' : '')}>{count}</span>}
      </a>
    </li>
  );
}

/* the CRM sidebar, shared by every scene mock (scene 1 pipeline, scene 4 dashboard) */
export function MockSidebar({
  dash,
  active,
  pipelineCount,
}: {
  dash: CultivDash;
  active: 'dashboard' | 'pipeline';
  pipelineCount: string;
}) {
  return (
    <aside className="s4d-side">
      <div className="s4d-brand">
        {/* eslint-disable-next-line @next/next/no-img-element -- fixed 24px decorative mark inside a scaled canvas */}
        <img src={`${MARKETING_CDN}/cultiv-symbol-orange.svg`} width={24} height={24} alt="" />
        <span className="s4d-brand-txt"><b>Cultiv</b><em>Gaston Wouters</em></span>
        <MockIcon id="chevron-down" s={16} />
      </div>
      <nav className="s4d-nav">
        <div className="s4d-group">
          <ul>
            <NavItem icon="gantt" label={dash.sideDashboard} on={active === 'dashboard'} />
          </ul>
        </div>
        <div className="s4d-group">
          <h2>{dash.sideWorkspace}</h2>
          <ul>
            <NavItem icon="folder-input" label={dash.sideToAssign} count="2" red />
            <NavItem icon="table" label={dash.sidePipeline} count={pipelineCount} on={active === 'pipeline'} />
            <NavItem icon="calendar" label={dash.sideCalendar} />
          </ul>
        </div>
        <div className="s4d-group">
          <h2>{dash.sideTools}</h2>
          <ul>
            <NavItem icon="search" label={dash.sideProspecting} />
            <NavItem icon="file-chart" label={dash.sideValuation} />
            <NavItem icon="file-pen" label={dash.sideCompromis} />
            <NavItem icon="workflow" label={dash.sideSequences} />
          </ul>
        </div>
      </nav>
      <div className="s4d-foot">
        <ul>
          <NavItem icon="settings" label={dash.sideSettings} />
          <NavItem icon="mail-question" label={dash.sideSendEmail} />
        </ul>
      </div>
    </aside>
  );
}

export function DashboardMock({ dash }: { dash: CultivDash }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(0); // 0 = not measured yet, canvas hidden

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // offsetWidth = layout width: correct even while an ancestor reveal-transform
    // is mid-animation (a getBoundingClientRect here bakes in the animated scale,
    // and ResizeObserver never refires on transforms — the wrong fit would stick)
    const fit = () => setScale(el.offsetWidth / 1680);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const money = dash.axisMoney; // bottom→top
  const stageBars = [
    // x of column group, bar path d, count, count y
    { x: 112, d: 'M89 320 v-217 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v217 z', fill: '#2563eb', n: '115', ny: 88 },
    { x: 222, d: 'M199 320 v-9 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v9 z', fill: '#000000', n: '8', ny: 296 },
    { x: 332, d: 'M309 320 v-17 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v17 z', fill: '#a855f7', n: '12', ny: 288 },
    { x: 442, d: 'M419 320 v-8 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v8 z', fill: '#ef4444', n: '7', ny: 297 },
    { x: 552, d: 'M529 320 v-2 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v2 z', fill: '#3e7c4f', n: '3', ny: 303 },
    { x: 662, d: 'M639 320 v-4 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v4 z', fill: '#ebda24', n: '3', ny: 301 },
  ];
  const monthX = [170, 452, 734, 1016, 1298, 1570];
  const points = [
    [170, 377, 9], [311, 346, 6], [452, 315, 9], [593, 282, 6], [734, 248, 9], [875, 215, 6], [1016, 184, 9],
  ];

  return (
    <div ref={rootRef} className="scene-canvas s4d" aria-hidden="true">
      <MockDefs />
      <div className="s4d-canvas" style={{ transform: `scale(${scale})`, visibility: scale ? 'visible' : 'hidden' }}>

        <MockSidebar dash={dash} active="dashboard" pipelineCount="148" />

        <main className="s4d-main">
          <div className="s4d-top">
            <span className="s4d-collapse">
              <i>
                <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
              </i>
            </span>
            <b className="kq-cap">{dash.sideDashboard}</b>
          </div>

          <nav className="s4d-tabsbar">
            <div className="s4d-seg">
              <button type="button" className="on">
                <MockIcon id="trending-up" s={14} /><span className="kq-cap">{dash.tabLeads}</span>
              </button>
              <button type="button">
                <MockIcon id="chart-column" s={14} /><span className="kq-cap">{dash.tabActivity}</span>
              </button>
            </div>
          </nav>

          <div className="s4d-body">
            <div className="s4d-cols">

              {/* ── Commissions & forecast ── */}
              <div className="s4d-card" style={{ height: 550 }}>
                <div className="s4d-chead">
                  <div className="s4d-chead-row">
                    <div className="s4d-ct">
                      <h3>{dash.comTitle}</h3>
                      <p>{dash.comSub}</p>
                    </div>
                    <div className="s4d-tools">
                      <div className="s4d-leg">
                        <span><span className="sw" style={{ background: '#E9551B' }} /><span className="kq-cap">{dash.legCommissions}</span></span>
                        <span><span className="sw" style={{ background: 'repeating-linear-gradient(90deg,#F5A483 0 5px,transparent 5px 9px)' }} /><span className="kq-cap">{dash.legForecast}</span></span>
                        <span><span className="sw" style={{ background: 'repeating-linear-gradient(90deg,#a8a29a 0 3px,transparent 3px 7px)' }} /><span className="kq-cap">{dash.legTarget}</span></span>
                      </div>
                      <div className="s4d-btns">
                        <button type="button" className="s4d-btn">
                          <MockIcon id="funnel" s={16} /><span className="kq-cap">{dash.filterAgents}</span>
                        </button>
                        <button type="button" className="s4d-btn">
                          <span className="kq-cap">{dash.periodYtd}</span><svg width={14} height={14} style={{ opacity: 0.5 }}><use href="#s4d-chevron-down" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="s4d-chart">
                  <svg viewBox="-20 -40 1660 516" role="img" aria-label={dash.comAria}>
                    <defs>
                      <clipPath id="s4d-fcclip">
                        <rect x="1010" y="-40" width="600" height="560" className="s4d-a-fc" style={{ transformOrigin: '1010px 0px' }} />
                      </clipPath>
                    </defs>

                    <g stroke="#eeece7" strokeWidth="1" className="s4d-a-grid">
                      {[420, 322, 225, 127, 30].map((y) => <line key={y} x1="130" y1={y} x2="1580" y2={y} />)}
                    </g>
                    <g fill="#71717a" fontSize="15" textAnchor="end" className="s4d-a-grid">
                      {[426, 328, 231, 133, 36].map((y, i) => <text key={y} x="112" y={y}>{money[i]}</text>)}
                    </g>
                    <g fill="#71717a" fontSize="15" textAnchor="middle" className="s4d-a-grid">
                      {monthX.map((x, i) => <text key={x} x={x} y="456">{dash.months[i]}</text>)}
                    </g>

                    <path d="M170 377 L311 346 L452 315 L593 282 L734 248 L875 215 L1016 184 L1016 420 L170 420 Z" fill="#E9551B" fillOpacity="0.07" className="s4d-a-fill" />

                    <polyline points="170,356 1580,30" fill="none" stroke="#a8a29a" strokeWidth="3" strokeDasharray="2 8" strokeLinecap="round" className="s4d-a-target" />

                    <g className="s4d-a-today">
                      <line x1="1016" y1="4" x2="1016" y2="420" stroke="#c9c5bd" strokeWidth="1.5" strokeDasharray="6 6" />
                      <text x="1016" y="-10" fill="#a8a29a" fontSize="14" letterSpacing="2" textAnchor="middle">{dash.today}</text>
                    </g>

                    <polyline points="170,377 311,346 452,315 593,282 734,248 875,215 1016,184" fill="none" stroke="#E9551B" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" pathLength={100} strokeDasharray="100" className="s4d-a-draw" />

                    <g clipPath="url(#s4d-fcclip)">
                      <polyline points="1016,184 1298,101 1580,18" fill="none" stroke="#F5A483" strokeWidth="5" strokeDasharray="14 10" strokeLinecap="round" />
                      <circle cx="1298" cy="101" r="9" fill="#fff" stroke="#F5A483" strokeWidth="4" />
                      <circle cx="1580" cy="18" r="10" fill="#fff" stroke="#F5A483" strokeWidth="4.5" />
                    </g>
                    <g className="s4d-a-radar-on">
                      <circle cx="1580" cy="18" r="19" fill="none" stroke="#E9551B" strokeWidth="2.5" className="s4d-a-radar" style={{ transformOrigin: '1580px 18px' }} />
                    </g>

                    <g fill="#E9551B">
                      {points.map(([cx, cy, r], i) => (
                        <circle key={i} cx={cx} cy={cy} r={r} className={`s4d-a-p${i + 1}`} style={{ transformOrigin: `${cx}px ${cy}px` }} />
                      ))}
                    </g>
                  </svg>
                </div>
                <div className="s4d-cfoot">
                  <div className="s4d-stats">
                    <div className="s4d-stat s4d-a-stat">
                      <span className="s4d-k">{dash.statMandates}</span>
                      <span className="v">{dash.statMandatesVal}</span>
                    </div>
                    <div className="s4d-stat s4d-a-stat">
                      <span className="s4d-k">{dash.statEarned}</span>
                      <span className="v" style={{ color: '#E9551B' }}>{dash.statEarnedVal}</span>
                    </div>
                    <div className="s4d-stat s4d-a-prog">
                      <span className="s4d-k">{dash.statForecast}</span>
                      <span className="v" style={{ color: '#F5A483' }}>{dash.statForecastVal}</span>
                    </div>
                  </div>
                  <div className="s4d-src s4d-a-src">
                    <div className="s4d-src-head">
                      <span className="s4d-k">{dash.perSource}</span>
                      <span className="t">{dash.perSourceTotal}</span>
                    </div>
                    <div className="s4d-bar s4d-a-bar">
                      <span style={{ flex: 69, background: '#E9551B' }} />
                      <span style={{ flex: 34.5, background: '#4A6FA5' }} />
                      <span style={{ flex: 17.5, background: '#C98A2F' }} />
                    </div>
                    <div className="s4d-src-chips s4d-a-chip">
                      <span><span className="dot" style={{ background: '#E9551B' }} /><span className="kq-cap">{dash.srcCultiv} · <strong>{dash.srcCultivVal}</strong></span></span>
                      <span><span className="dot" style={{ background: '#4A6FA5' }} /><span className="kq-cap">{dash.srcProspecting} · <strong>{dash.srcProspectingVal}</strong></span></span>
                      <span><span className="dot" style={{ background: '#C98A2F' }} /><span className="kq-cap">{dash.srcInbound} · <strong>{dash.srcInboundVal}</strong></span></span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="s4d-grid2">

                {/* ── Leads per funnel stage ── */}
                <div className="s4d-card col">
                  <div className="s4d-chead deep">
                    <div className="s4d-chead-row">
                      <div className="s4d-ct">
                        <h3>{dash.funTitle}</h3>
                        <p>{dash.funSub}</p>
                      </div>
                      <div className="s4d-tools">
                        <span className="s4d-chip" style={{ background: '#E9551B' }}><span className="kq-cap">{dash.funChip}</span></span>
                        <button type="button" className="s4d-btn">
                          <MockIcon id="funnel" s={16} /><span className="kq-cap">{dash.filterStages}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="s4d-pad24">
                    <svg viewBox="0 0 760 390" role="img" aria-label={dash.funAria}>
                      <g stroke="#e5e5e5" strokeDasharray="3 3" strokeWidth="1">
                        {[320, 264, 208, 152, 96, 40].map((y) => <line key={y} x1="60" y1={y} x2="740" y2={y} />)}
                      </g>
                      <g fill="#71717a" fontSize="13" textAnchor="end">
                        {[['0', 325], ['27', 269], ['53', 213], ['80', 157], ['106', 101], ['133', 45]].map(([n, y]) => <text key={y} x="48" y={y}>{n}</text>)}
                      </g>
                      {stageBars.map((b, i) => (
                        <g key={i}>
                          <path d={b.d} fill={b.fill} />
                          <text x={b.x} y={b.ny} fill="#282828" fontSize="14" fontWeight="700" textAnchor="middle">{b.n}</text>
                          <text x={b.x} y="344" fill="#71717a" fontSize="12" textAnchor="middle">{dash.stages[i]}</text>
                        </g>
                      ))}
                    </svg>
                  </div>
                </div>

                {/* ── Lead attribution ── */}
                <div className="s4d-card col">
                  <div className="s4d-chead deep">
                    <div className="s4d-chead-row">
                      <div className="s4d-ct">
                        <h3>{dash.attrTitle}</h3>
                        <p>{dash.attrSub}</p>
                      </div>
                      <div className="s4d-tools">
                        <span className="s4d-chip" style={{ background: '#000' }}><span className="kq-cap">{dash.attrChip}</span></span>
                        <button type="button" className="s4d-btn">
                          <span className="kq-cap">{dash.period30}</span><svg width={14} height={14} style={{ opacity: 0.5 }}><use href="#s4d-chevron-down" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="s4d-attr-body">
                    <svg viewBox="0 0 620 390" role="img" aria-label={dash.attrAria}>
                      <g stroke="#e5e5e5" strokeDasharray="3 3" strokeWidth="1">
                        {[320, 264, 208, 152, 96, 40].map((y) => <line key={y} x1="60" y1={y} x2="600" y2={y} />)}
                      </g>
                      <g fill="#71717a" fontSize="13" textAnchor="end">
                        {[['0', 325], ['4', 269], ['8', 213], ['12', 157], ['16', 101], ['20', 45]].map(([n, y]) => <text key={y} x="48" y={y}>{n}</text>)}
                      </g>
                      <g>
                        <path d="M292 320 v-232 a6 6 0 0 1 6 -6 h34 a6 6 0 0 1 6 6 v232 z" fill="#4C9A9A" />
                        <text x="315" y="72" fill="#282828" fontSize="14" fontWeight="700" textAnchor="middle">17</text>
                        <text x="315" y="344" fill="#71717a" fontSize="12" textAnchor="middle">{dash.attrDirect}</text>
                      </g>
                    </svg>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </main>

      </div>
    </div>
  );
}
